import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { GitBranch, Trash2, Copy, AlertTriangle } from 'lucide-react';
import { validateConditionNode } from '../../utils/workflowValidation';

const ConditionNode = ({ data, isConnectable }) => {
  // Validate the node configuration
  const validation = validateConditionNode(data);
  const hasErrors = !validation.isValid;

  const handleDoubleClick = () => {
    if (data.onConfigClick) {
      data.onConfigClick();
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (data.onDelete && window.confirm('Delete this condition node?')) {
      data.onDelete();
    }
  };

  const handleCopy = (e) => {
    e.stopPropagation();
    if (data.onCopy) {
      data.onCopy();
    }
  };

  return (
    <div 
      className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-br from-yellow-500 to-orange-500 min-w-[200px] cursor-pointer hover:shadow-xl transition-shadow relative group ${
        hasErrors ? 'border-2 border-red-400 ring-2 ring-red-300' : 'border-2 border-yellow-400'
      }`}
      onDoubleClick={handleDoubleClick}
      title={hasErrors ? `⚠️ ${validation.errors.join(', ')}` : 'Double-click to configure'}
    >
      {/* Validation Warning Badge */}
      {hasErrors && (
        <div className="absolute -top-3 -left-3 z-10 group/warning">
          <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center shadow-md animate-pulse">
            <AlertTriangle className="w-4 h-4 text-white" />
          </div>
          {/* Tooltip */}
          <div className="absolute left-8 top-0 w-48 bg-red-600 text-white text-xs rounded-lg px-3 py-2 shadow-lg opacity-0 invisible group-hover/warning:opacity-100 group-hover/warning:visible transition-all z-50">
            <div className="font-semibold mb-1">Configuration needed:</div>
            <ul className="list-disc list-inside">
              {validation.errors.map((error, i) => (
                <li key={i}>{error}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Copy Button */}
      <button
        onClick={handleCopy}
        className="absolute -top-2 -right-10 w-6 h-6 bg-blue-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-blue-600 flex items-center justify-center"
        title="Copy node"
      >
        <Copy className="w-3 h-3" />
      </button>
      
      {/* Delete Button */}
      <button
        onClick={handleDelete}
        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 flex items-center justify-center"
        title="Delete node"
      >
        <Trash2 className="w-3 h-3" />
      </button>
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
        className="w-3 h-3 !bg-white border-2 border-yellow-500"
      />
      
      <div className="flex items-center gap-2 mb-1">
        <GitBranch className="w-4 h-4 text-white" />
        <div className="text-xs font-semibold text-white uppercase">Condition</div>
      </div>
      <div className="font-medium text-white text-sm">{data.label}</div>
      {data.conditionType && (
        <div className="text-xs text-yellow-100 mt-1">{data.conditionType.replace(/_/g, ' ')}</div>
      )}
      {!hasErrors && data.conditionConfig && Object.keys(data.conditionConfig).length > 0 && (
        <div className="text-xs text-green-200 mt-1">
          ✓ Configured
        </div>
      )}
      {hasErrors && (
        <div className="text-xs text-red-200 mt-1">
          ⚠️ Needs configuration
        </div>
      )}
      
      <div className="flex gap-4 mt-2">
        <Handle
          type="source"
          position={Position.Bottom}
          id="true"
          isConnectable={isConnectable}
          className="w-3 h-3 !bg-green-500 border-2 border-white"
          style={{ left: '30%' }}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="false"
          isConnectable={isConnectable}
          className="w-3 h-3 !bg-red-500 border-2 border-white"
          style={{ left: '70%' }}
        />
      </div>
      <div className="flex justify-between text-xs text-white mt-1 px-2">
        <span>Yes</span>
        <span>No</span>
      </div>
    </div>
  );
};

export default memo(ConditionNode);